import api from './api';
import { AxiosResponse } from 'axios';

export type ButtonComponent = {
  type: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';
  text: string;
  url?: string;
  phone_number?: string;
};

export interface TemplateComponents {
  header: { enabled: boolean; format: 'TEXT'; text: string };
  body: { text: string };
  footer: { enabled: boolean; text: string };
  buttons: ButtonComponent[];
}

export type VariableMappingRow = {
  section: 'header' | 'body' | 'footer' | 'button';
  param_index?: number;
  name?: string;
  source_key: string;
  button_index?: number;
  url_param_index?: number;
};

// Types for WhatsApp Template API
export interface WhatsAppTemplate {
  id: number;
  name: string;
  content: string;
  template_category?: 'UTILITY' | 'MARKETING' | 'AUTHENTICATION';
  parameter_format?: 'POSITIONAL' | 'NAMED';
  components?: TemplateComponents;
  variable_mapping?: VariableMappingRow[];
  meta_template_id?: string;
  meta_template_name?: string;
  language_code?: string;
  body_parameter_sources?: string[];
  meta_raw?: Record<string, unknown>;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

// WhatsApp Template service functions
const whatsappTemplateService = {
  // Get all WhatsApp templates
  getAll: async (params?: Record<string, unknown>): Promise<WhatsAppTemplate[]> => {
    try {
      const response: AxiosResponse = await api.get('/whatsapp-templates/', { params });

      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data && Array.isArray(response.data.results)) {
        return response.data.results;
      } else {
        return [];
      }
    } catch (error) {
      console.error('Error fetching WhatsApp templates:', error);
      throw error;
    }
  },

  // Get a single WhatsApp template by ID
  getById: async (id: number): Promise<WhatsAppTemplate> => {
    if (isNaN(id)) {
      throw new Error(`Invalid template ID: ${id}`);
    }
    const response: AxiosResponse = await api.get(`/whatsapp-templates/${id}/`);
    return response.data;
  },

  // Create a new WhatsApp template
  create: async (template: Partial<WhatsAppTemplate>): Promise<WhatsAppTemplate> => {
    const response: AxiosResponse = await api.post('/whatsapp-templates/', template);
    return response.data;
  },

  // Update an existing WhatsApp template
  update: async (id: number, template: Partial<WhatsAppTemplate>): Promise<WhatsAppTemplate> => {
    if (isNaN(id)) {
      throw new Error(`Invalid template ID: ${id}`);
    }
    const response: AxiosResponse = await api.put(`/whatsapp-templates/${id}/`, template);
    return response.data;
  },

  // Delete a WhatsApp template
  delete: async (id: number): Promise<void> => {
    if (isNaN(id)) {
      throw new Error(`Invalid template ID: ${id}`);
    }
    await api.delete(`/whatsapp-templates/${id}/`);
  },

  createWhatsAppTemplate: async (data: Partial<WhatsAppTemplate>) => {
    const response = await api.post<WhatsAppTemplate>('/whatsapp-templates/', data);
    return response.data;
  },

  updateWhatsAppTemplate: async (id: number, data: Partial<WhatsAppTemplate>) => {
    const response = await api.patch<WhatsAppTemplate>(`/whatsapp-templates/${id}/`, data);
    return response.data;
  },

  syncStatus: async (id: number) => {
    const response = await api.post(`/whatsapp-templates/${id}/sync_status/`);
    return response.data;
  },

  syncAll: async () => {
    const response = await api.post<{
      status: string;
      data: { updated: number; errors: { id: number; message: string }[] };
    }>('/whatsapp-templates/sync_all/');
    return response.data;
  },
};

export default whatsappTemplateService;
